import { useEffect } from 'react';

import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { GMetrixTabs, GMetrixTabsTab } from 'components/GMetrixTabs';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { LtiCategory, PartnerApi } from 'apis/PartnerApi';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSuperState } from 'hooks';

export type ResourceSelectionType = 'test' | 'course';

export type ResourceSelection = {
    name: string;
    deliverableControl: string;
    type: ResourceSelectionType;
};

type ResourceKeyBrowserMode = 'lookup' | 'select';

type CategoryRowProps = {
    category: LtiCategory;
    mode: ResourceKeyBrowserMode;
    onSelect?: (selection: ResourceSelection) => void;
    selected?: ResourceSelection | null;
    type: 'tests' | 'courses';
};

const CategoryRow = (props: CategoryRowProps) => {

    const { category, mode, onSelect, selected, type } = props;

    const [state, setState] = useSuperState({
        expanded: false
    });

    const { expanded } = state;

    const toggleExpanded = () => {

        setState({ expanded: !expanded });

    };

    return (
        <div className="mb-3">
            <div
                style={{
                    background: expanded ? '#f8fafc' : '#f3f5f7',
                    border: '1px solid #d8e0e7',
                    borderLeft: '4px solid #6d8b95',
                    borderRadius: 8,
                    color: '#25313a',
                    cursor: 'pointer',
                    fontSize: '1rem'
                }}
                className="d-flex align-items-center px-3 py-2 shadow-sm"
                onClick={toggleExpanded}
            >
                <i
                    className={`fa-solid fa-chevron-right me-2 ${expanded ? 'fa-rotate-90' : ''}`}
                    style={{ color: '#6d8b95' }}
                />
                {category.IconURL && (
                    <img
                        style={{
                            background: '#fff',
                            border: '1px solid #d8e0e7',
                            borderRadius: 6,
                            height: 30,
                            marginRight: 10,
                            padding: 3,
                            width: 30
                        }}
                        alt=""
                        src={category.IconURL}
                    />
                )}
                <strong>
                    {category.Name}
                </strong>
            </div>

            {expanded && type === 'tests' && category.Products?.map(product => (
                <ProductRow
                    key={product.Id}
                    mode={mode}
                    onSelect={onSelect}
                    product={product}
                    selected={selected}
                />
            ))}

            {expanded && type === 'courses' && category.Courses?.map(course => (
                <CourseRow
                    course={course}
                    key={course.Id}
                    mode={mode}
                    onSelect={onSelect}
                    selected={selected}
                />
            ))}
        </div>
    );

};

type ProductRowProps = {
    mode: ResourceKeyBrowserMode;
    onSelect?: (selection: ResourceSelection) => void;
    product: LtiCategory['Products'][number];
    selected?: ResourceSelection | null;
};

const ProductRow = (props: ProductRowProps) => {

    const { mode, onSelect, product, selected } = props;

    const [state, setState] = useSuperState({
        expanded: false
    });

    const { expanded } = state;

    const toggleExpanded = () => {

        setState({ expanded: !expanded });

    };

    return (
        <div className="ms-3 mt-2">
            <div
                style={{
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                    color: '#334155',
                    cursor: 'pointer',
                    fontSize: 13,
                    minHeight: 50
                }}
                className="d-flex align-items-center px-3 py-2"
                onClick={toggleExpanded}
            >
                <i
                    className={`fa-solid fa-chevron-right me-2 ${expanded ? 'fa-rotate-90' : ''}`}
                    style={{ color: '#7b8794' }}
                />
                <span
                    style={{
                        alignItems: 'center',
                        background: product.Color ? `#${product.Color}` : '#94a3b8',
                        borderRadius: 6,
                        display: 'inline-flex',
                        height: 28,
                        justifyContent: 'center',
                        width: 28
                    }}
                    className="me-2"
                >
                    {product.IconURL && (
                        <img
                            alt=""
                            src={product.IconURL}
                            style={{ height: 20, padding: 4, width: 20 }}
                        />
                    )}
                </span>
                {product.Name}
            </div>

            {expanded && product.PracticeTests?.map((test, idx) => {

                const isSelected = mode === 'select' && selected?.type === 'test' && selected?.deliverableControl === test.DeliverableControl;

                return (
                    <div
                        style={{
                            background: isSelected ? '#e6f4f1' : '#f8fafc',
                            border: `1px solid ${isSelected ? '#0f766e' : '#e2e8f0'}`,
                            borderRadius: 6,
                            color: '#334155',
                            fontSize: 12,
                            minHeight: 36
                        }}
                        className="d-flex align-items-center ps-5 pe-3 mt-1"
                        key={idx}
                    >
                        <span style={{ flex: 1 }}>
                            {test.Name}
                        </span>
                        <span
                            style={{
                                background: '#e2e8f0',
                                borderRadius: 4,
                                color: '#334155',
                                fontWeight: 600,
                                padding: '2px 8px',
                                width: 'fit-content'
                            }}
                        >
                            {test.DeliverableControl}
                        </span>
                        {mode === 'select' && (
                            <GMetrixButton
                                className="ms-2"
                                onClick={() => onSelect?.({ name: test.Name, deliverableControl: test.DeliverableControl, type: 'test' })}
                                variant={isSelected ? 'success' : 'primary'}
                            >
                                {isSelected ? <FormattedMessage defaultMessage="Selected" id="selected.2c7e9011" /> : <FormattedMessage defaultMessage="Select" id="select.57e6e9c7" />}
                            </GMetrixButton>
                        )}
                    </div>
                );

            })}
        </div>
    );

};

type CourseRowProps = {
    course: LtiCategory['Courses'][number];
    mode: ResourceKeyBrowserMode;
    onSelect?: (selection: ResourceSelection) => void;
    selected?: ResourceSelection | null;
};

const CourseRow = (props: CourseRowProps) => {

    const { course, mode, onSelect, selected } = props;

    const [state, setState] = useSuperState({
        accessCode: '',
        showDownloadModal: false
    });

    const { accessCode, showDownloadModal } = state;

    const isSelected = mode === 'select' && selected?.type === 'course' && selected?.deliverableControl === course.DeliverableControl;

    const openDownloadModal = () => {

        setState({ accessCode: '', showDownloadModal: true });

    };

    const closeDownloadModal = () => {

        setState({ showDownloadModal: false });

    };

    const handleDownload = () => {

        const url = PartnerApi.GetDownloadCartridgeUrl(course.Id, accessCode);
        window.location.href = url;
        closeDownloadModal();

    };

    return (
        <>
            <div
                style={{
                    background: isSelected ? '#e6f4f1' : '#fff',
                    border: `1px solid ${isSelected ? '#0f766e' : '#e2e8f0'}`,
                    borderRadius: 8,
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                    color: '#334155',
                    fontSize: 12,
                    minHeight: 38
                }}
                className="d-flex align-items-center ps-4 pe-3 mt-2"
            >
                <span style={{ flex: 1 }}>
                    {course.Name}
                </span>
                <span
                    style={{
                        background: '#e2e8f0',
                        borderRadius: 4,
                        fontWeight: 600,
                        marginRight: 12,
                        padding: '2px 8px'
                    }}
                >
                    {course.DeliverableControl}
                </span>
                {mode === 'lookup' && (
                    <span style={{ width: 100 }}>
                        {course.HasCommonCartridge && (
                            <a
                                onClick={e => {

                                    e.preventDefault(); openDownloadModal();

                                }}
                                href="#"
                                style={{ color: '#0f766e', fontWeight: 600, textDecoration: 'underline' }}
                            >
                                <FormattedMessage defaultMessage="Download" id="download.5e1e7483" />
                            </a>
                        )}
                    </span>
                )}
                {mode === 'select' && (
                    <GMetrixButton
                        onClick={() => onSelect?.({ name: course.Name, deliverableControl: course.DeliverableControl, type: 'course' })}
                        variant={isSelected ? 'success' : 'primary'}
                    >
                        {isSelected ? <FormattedMessage defaultMessage="Selected" id="selected.2c7e9011" /> : <FormattedMessage defaultMessage="Select" id="select.57e6e9c7" />}
                    </GMetrixButton>
                )}
            </div>

            {mode === 'lookup' && (
                <GMetrixDialog
                    actionButtonClick={handleDownload}
                    actionButtonDisabled={false}
                    actionButtonText={formatMessage({ id: 'download.5e1e7483', defaultMessage: 'Download' })}
                    dialogLabel={formatMessage({ id: 'download_common_cartridge_v13.688131ac', defaultMessage: 'Download Common Cartridge (v1.3)' })}
                    onCancel={closeDownloadModal}
                    show={showDownloadModal}
                    size="lg"
                >
                    <p>
                        <FormattedMessage defaultMessage="Include an access code in your cartridge? If you choose to include an access code, the cartridge that is generated will no longer be generic, and you should not distribute the cartridge outside of your organization." id="include_an_access_code_in_your_cartridge.c20ad03a" />
                    </p>
                    <GMetrixInput
                        label={formatMessage({ id: 'use_access_code.992e35f6', defaultMessage: 'Use Access Code' })}
                        onChange={e => setState({ accessCode: e.currentTarget.value })}
                        value={accessCode}
                    />
                </GMetrixDialog>
            )}
        </>
    );

};

type ResourceKeyBrowserProps = {
    mode?: ResourceKeyBrowserMode;
    onSelect?: (selection: ResourceSelection) => void;
    partnerId: number;
    selected?: ResourceSelection | null;
};

export const ResourceKeyBrowser = (props: ResourceKeyBrowserProps) => {

    const { mode = 'lookup', onSelect, partnerId, selected } = props;

    const { formatMessage } = useIntl();

    const [state, setState] = useSuperState({
        activeTab: 'tests' as 'tests' | 'courses',
        courseCategories: [] as LtiCategory[],
        courseSearchQuery: '',
        customTestCategories: [] as LtiCategory[],
        loading: true,
        systemTestCategories: [] as LtiCategory[],
        testSearchQuery: ''
    });

    const {
        activeTab,
        courseCategories,
        courseSearchQuery,
        customTestCategories,
        loading,
        systemTestCategories,
        testSearchQuery
    } = state;

    const loadData = async () => {

        try {

            setState({ loading: true });

            const [systemTests, customTests, courses] = await Promise.all([
                PartnerApi.GetTestKeys(partnerId),
                PartnerApi.GetCustomTestKeys(partnerId),
                PartnerApi.GetCourseKeys(partnerId)
            ]);

            setState({
                courseCategories: courses,
                customTestCategories: customTests,
                loading: false,
                systemTestCategories: systemTests
            });

        } catch {

            setState({ loading: false });

        }

    };

    useEffect(() => {

        if (!partnerId) return;
        loadData();

    }, [partnerId]);

    const includesText = (value: string | undefined, query: string) => (value || '').toLowerCase().includes(query);

    const filterTestCategories = (categories: LtiCategory[], query: string) => {

        if (!query) {

            return categories;

        }

        return categories.filter(category => {

            const matchesCategory = includesText(category.Name, query);
            const matchesProducts = category.Products?.some(product => {

                const matchesProduct = includesText(product.Name, query);
                const matchesPracticeTests = product.PracticeTests?.some(test => includesText(test.Name, query) || includesText(test.DeliverableControl, query));

                return matchesProduct || matchesPracticeTests;

            });

            return matchesCategory || matchesProducts;

        });

    };

    const filterCourseCategories = (categories: LtiCategory[], query: string) => {

        if (!query) {

            return categories;

        }

        return categories.filter(category => {

            const matchesCategory = includesText(category.Name, query);
            const matchesCourses = category.Courses?.some(course => includesText(course.Name, query) || includesText(course.DeliverableControl, query));

            return matchesCategory || matchesCourses;

        });

    };

    const normalizedTestSearchQuery = testSearchQuery.trim().toLowerCase();
    const normalizedCourseSearchQuery = courseSearchQuery.trim().toLowerCase();

    const filteredSystemTestCategories = filterTestCategories(systemTestCategories, normalizedTestSearchQuery);
    const filteredCustomTestCategories = filterTestCategories(customTestCategories, normalizedTestSearchQuery);
    const filteredCourseCategories = filterCourseCategories(courseCategories, normalizedCourseSearchQuery);

    return (
        <GMetrixTabs
            className="mb-3"
            onSelect={index => setState({ activeTab: index === 0 ? 'tests' : 'courses' })}
            selected={activeTab === 'tests' ? 0 : 1}
        >
            <GMetrixTabsTab title={formatMessage({ id: 'test_keys.a509ff54', defaultMessage: 'Test Keys' })}>
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">
                                <FormattedMessage defaultMessage="Loading..." id="loading.2350c7f9" />
                            </span>
                        </div>
                    </div>
                ) : (
                    <div>
                        <div className="mb-3">
                            <GMetrixInput
                                label={formatMessage({ id: 'search_resources.eb126fb8', defaultMessage: 'Search resources' })}
                                onChange={e => setState({ testSearchQuery: e.currentTarget.value })}
                                placeholder={formatMessage({ id: 'search_categories_products_or_tests.22760ccb', defaultMessage: 'Search categories, products, or tests' })}
                                value={testSearchQuery}
                            />
                        </div>

                        <div className="d-flex gap-4">
                            <div style={{ flex: 1 }}>
                                <h5>
                                    <FormattedMessage defaultMessage="GMetrix Practice Tests" id="gmetrix_practice_tests.2a74a192" />
                                </h5>
                                {filteredSystemTestCategories.map(cat => (
                                    <CategoryRow
                                        category={cat}
                                        key={cat.Id}
                                        mode={mode}
                                        onSelect={onSelect}
                                        selected={selected}
                                        type="tests"
                                    />
                                ))}
                            </div>
                            <div style={{ flex: 1 }}>
                                <h5>
                                    <FormattedMessage defaultMessage="Custom Tests" id="custom_tests.9e7494ea" />
                                </h5>
                                {filteredCustomTestCategories.map(cat => (
                                    <CategoryRow
                                        category={cat}
                                        key={cat.Id}
                                        mode={mode}
                                        onSelect={onSelect}
                                        selected={selected}
                                        type="tests"
                                    />
                                ))}
                            </div>
                        </div>

                        {filteredSystemTestCategories.length === 0 && filteredCustomTestCategories.length === 0 && (
                            <div className="text-muted mt-3">
                                <FormattedMessage defaultMessage="No test resources match your search." id="no_test_resources_match_your_search.37ea21db" />
                            </div>
                        )}
                    </div>
                )}
            </GMetrixTabsTab>
            <GMetrixTabsTab title={formatMessage({ id: 'course_keys.ec64022f', defaultMessage: 'Course Keys' })}>
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">
                                <FormattedMessage defaultMessage="Loading..." id="loading.2350c7f9" />
                            </span>
                        </div>
                    </div>
                ) : (
                    <div>
                        <div className="mb-3">
                            <GMetrixInput
                                label={formatMessage({ id: 'search_course_resources.ff64901f', defaultMessage: 'Search course resources' })}
                                onChange={e => setState({ courseSearchQuery: e.currentTarget.value })}
                                placeholder={formatMessage({ id: 'search_categories_or_courses.afe69149', defaultMessage: 'Search categories or courses' })}
                                value={courseSearchQuery}
                            />
                        </div>

                        <h5>
                            <FormattedMessage defaultMessage="Courses" id="courses.c269a3fa" />
                        </h5>
                        {filteredCourseCategories.map(cat => (
                            <CategoryRow
                                category={cat}
                                key={cat.Id}
                                mode={mode}
                                onSelect={onSelect}
                                selected={selected}
                                type="courses"
                            />
                        ))}

                        {filteredCourseCategories.length === 0 && (
                            <div className="text-muted mt-3">
                                <FormattedMessage defaultMessage="No course resources match your search." id="no_course_resources_match_your_search.2741992e" />
                            </div>
                        )}
                    </div>
                )}
            </GMetrixTabsTab>
        </GMetrixTabs>
    );

};
